import styled, { css } from 'styled-components';

import { colors, media } from '../../../../core';

// types
export type NavLinkMode = 'light' | 'flex';

// interfaces
interface NavLinkProps {
  addMargin?: boolean;
  mode?: NavLinkMode;
}

interface SubMenuIsOpenProps {
  subMenuIsOpen: boolean;
}

const handleNavLinkMode = (mode: NavLinkMode) => {
  switch (mode) {
    case 'light':
      return css`
        color: ${({ theme }) => theme.navLinkColor || colors.darkCerulean};
      `;

    case 'flex':
      return css`
        color: ${colors.gunPowder};
        a {
          display: flex !important;
          align-items: center;
          span {
            margin-right: 1rem;
          }
        }
      `;

    default:
      return css`
        color: ${colors.gunPowder};
      `;
  }
};

const baseAfterElementStyles = css`
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  height: 0.25rem;
  width: 100%;
  background-color: ${({ theme }) => theme.navLinkBackgroundColor || colors.darkCerulean};
  border-radius: 0.125rem 0.125rem 0 0;
  transition: opacity 175ms ease-in-out;
  ${media.tabletMedium} {
    display: none;
  }
`;

export const NavLink = styled.li<NavLinkProps>`
  ${({ mode }) => (mode ? handleNavLinkMode(mode) : `color: ${colors.gunPowder};`)}
  font-weight: 500;
  ${({ addMargin }) => (addMargin ? 'margin-right: 1.5rem;' : '')}
  position: relative;
  &::after {
    ${baseAfterElementStyles}
    opacity: 0;
  }
  @media screen and (min-width: 901px) {
    &:hover,
    &:focus-within {
      color: ${({ theme }) => theme.navLinkColor || colors.darkCerulean};
      ul {
        opacity: 1;
        pointer-events: all;
        z-index: 1;
      }
      &::after {
        opacity: 1;
      }
    }
  }
  ${media.tabletMedium} {
    font-size: 1.33rem;
  }
`;

export const StyledLink = styled.a`
  &.active {
    &::after {
      ${baseAfterElementStyles}
      opacity: 1;
    }
  }
`;

export const NavLinkInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  div {
    flex: 1;
    svg {
      margin-left: auto;
    }
  }
`;

const TRANSITION_CLOSING = 'max-height 400ms ease-in-out 0ms, opacity 400ms ease-in-out 400ms';
const TRANSITION_OPENING = 'max-height 400ms ease-in-out 400ms, opacity 400ms ease-in-out 0ms';

export const NavSubMenu = styled.ul<SubMenuIsOpenProps>`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: ${({ theme }) => theme.navSubMenuItemHoverBackgroundColor || colors.whiteIce};
  border-radius: 0 0 0.625rem 0.625rem;
  box-shadow: inset 0 0.375rem 0.5rem -0.375rem rgb(0, 0, 0, 5%);
  min-width: 15.5rem;
  opacity: 0;
  pointer-events: none;
  transition: all 175ms ease-in-out;
  white-space: nowrap;
  z-index: -1;
  ${media.tabletMedium} {
    background-color: transparent;
    border-top: 1px solid ${({ theme }) => theme.navBorderColor || colors.edgewater};
    border-bottom: 1px solid ${({ theme }) => theme.navBorderColor || colors.edgewater};
    max-height: ${({ subMenuIsOpen }) => (subMenuIsOpen ? '15rem' : '0rem')};
    opacity: ${({ subMenuIsOpen }) => (subMenuIsOpen ? 1 : 0)};
    pointer-events: ${({ subMenuIsOpen }) => (subMenuIsOpen ? 'all' : 'none')};
    position: static;
    transition: ${({ subMenuIsOpen }) => (subMenuIsOpen ? TRANSITION_CLOSING : TRANSITION_OPENING)};
    z-index: 0;
  }
`;

export const NavSubLink = styled.li`
  color: ${({ theme }) => theme.navLinkColor || colors.darkCerulean};
  font-size: 0.875rem;
  font-weight: 400;
  text-transform: uppercase;
  transition: font-weight 150ms ease-in-out;
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.navBorderColor || colors.edgewater};
  }
  a {
    padding: 1.125rem 2.5rem 1.125rem 1rem !important;
    width: 100%;
  }
  ${media.tabletMedium} {
    font-size: 1rem;
  }
  @media (hover) {
    &:hover,
    &:active,
    &:focus {
      font-weight: 600;
    }
  }
`;

export const ChevronWrapper = styled.div<SubMenuIsOpenProps>`
  padding: 1.625rem 0;
  svg {
    transform: ${({ subMenuIsOpen }) => (subMenuIsOpen ? 'rotate(90deg)' : 'rotate(0)')};
  }
`;

export const MobileNavChevronWrapper = styled.svg`
  display: none;
  ${media.tabletMedium} {
    display: block;
    transition: transform 400ms ease-in-out;
  }
`;
